import React, {
    createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
    emptyState, fromUrlParams, isValidRange, localStorageKey, toUrlParams,
} from './filter-key';
import { computePreset } from '../../AnalyticsPro/DateRangeControl';

const DashboardFiltersContext = createContext(null);

const DEFAULT_PRESET = 'ytd';

// During an SPA route transition (route animation), the outgoing route's provider
// stays mounted alongside the incoming one. Both providers sync the SAME URL search
// params, and both also listen to URL changes — so each write by one triggers a
// re-write by the other, an infinite navigate ping-pong that freezes the page
// (most visible going to /v3/tenant, whose filter set differs from /v3/dashboard).
// The most-recently-mounted provider claims `activeLayoutKey`; stale providers then
// skip all URL reads/writes so only one provider ever owns the URL.
let activeLayoutKey = null;

// One-time migration: the smart-dashboard default date changed from Last-30d to
// YTD. Existing users have a persisted dateRange in localStorage, which hydrate()
// prefers over DEFAULT_PRESET — so without this they'd stay on the old default
// forever. Strip the saved dateRange once (keeping property/tenant selections)
// so the new YTD default takes effect; a flag stops it from re-running and
// clobbering dates the user picks afterwards. Runs at import, before any provider
// mounts and reads localStorage.
(function migrateDashboardDateDefault() {
    try {
        const FLAG = 'v3.dashboard.dateDefault.v2-ytd';
        if (localStorage.getItem(FLAG)) return;
        Object.keys(localStorage)
            .filter(k => k.startsWith('v3.dashboard.filters'))
            .forEach(k => {
                try {
                    const parsed = JSON.parse(localStorage.getItem(k));
                    if (parsed && parsed.dateRange) {
                        delete parsed.dateRange;
                        localStorage.setItem(k, JSON.stringify(parsed));
                    }
                } catch { /* skip malformed entry */ }
            });
        localStorage.setItem(FLAG, '1');
    } catch { /* localStorage unavailable — non-fatal */ }
})();

/**
 * Holds the canonical filter state for the smart dashboard.
 *
 *  Source of truth:    URL search params
 *  Persistence:        localStorage[v3.dashboard.filters.<layoutKey>]  (mirror)
 *  Tab sync:           `storage` event (matches useAuth.js pattern)
 *  Hydration sentinel: `filtersReady` is false for one tick during boot so
 *                      widgets render skeletons instead of firing requests with
 *                      defaults that are about to be replaced.
 *
 *  `concessionaireId` is intentionally NOT in this state — backend injects it
 *  from req.user. Sending it from the client would be ignored anyway.
 */
export function DashboardFiltersProvider({ children, layoutKey: layoutKeyProp }) {
    const params = useParams();
    const layoutKey = layoutKeyProp || params.layoutKey || 'default';

    const { propertyAccess } = useAuth();
    const accessibleIds = useMemo(() => splitAccess(propertyAccess), [propertyAccess]);
    // Empty propertyAccess means "all properties under the concessionaire"
    // (admin / sadmin / unrestricted roles). When false, sanitize() does NOT
    // intersect URL/localStorage property IDs — they're kept verbatim.
    const hasExplicitPropertyScope = accessibleIds.length > 0;

    const navigate = useNavigate();
    const location = useLocation();

    // Claim ownership of the URL for this layout. useLayoutEffect runs before the
    // passive effects below in the same commit, so the incoming provider marks
    // itself active before the outgoing provider's URL effects fire and bail out.
    useLayoutEffect(() => {
        activeLayoutKey = layoutKey;
    }, [layoutKey]);
    const isActive = () => !activeLayoutKey || activeLayoutKey === layoutKey;

    // `filters` drives the UI immediately.
    // `filtersReady` flips to true after the first hydration so widgets don't
    // fetch with stale defaults during URL/localStorage rehydration.
    const [filters, setFilters] = useState(emptyState);
    const [filtersReady, setFiltersReady] = useState(false);

    // Suppress one URL→state echo immediately after we write to the URL ourselves.
    const skipNextUrlSync = useRef(false);

    // ── Hydration: URL > localStorage > default ────────────────────────────
    useEffect(() => {
        const hydrated = hydrate(location.search, layoutKey, accessibleIds, hasExplicitPropertyScope);
        setFilters(hydrated);
        setFiltersReady(true);
        // If hydration produced state that the URL doesn't reflect (came from
        // localStorage or the default preset), push it back into the URL so
        // refresh / share works.
        const urlFragment = toUrlParams(hydrated);
        if (isActive() && urlFragment !== stripQuestion(location.search)) {
            skipNextUrlSync.current = true;
            navigate({ pathname: location.pathname, search: urlFragment ? `?${urlFragment}` : '' }, { replace: true });
        }
    // Deliberately only on layoutKey + propertyAccess change (treat URL as
    // an external trigger handled by the popstate effect below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutKey, propertyAccess]);

    // ── External URL changes (back/forward) → state ────────────────────────
    useEffect(() => {
        if (!filtersReady) return;
        // Only the active provider reacts to URL changes — a stale provider left
        // mounted during a route transition must not echo the new route's URL.
        if (!isActive()) return;
        if (skipNextUrlSync.current) {
            skipNextUrlSync.current = false;
            return;
        }
        const next = sanitize(fromUrlParams(location.search), accessibleIds, hasExplicitPropertyScope);
        setFilters(prev => sameShape(prev, next) ? prev : next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // ── State → URL + localStorage ─────────────────────────────────────────
    useEffect(() => {
        if (!filtersReady) return;
        // localStorage is keyed per layout (safe to mirror), but the URL is shared —
        // only the active provider may write it, else two providers ping-pong forever.
        if (isActive()) {
            const urlFragment = toUrlParams(filters);
            const currentFragment = stripQuestion(location.search);
            if (urlFragment !== currentFragment) {
                skipNextUrlSync.current = true;
                navigate(
                    { pathname: location.pathname, search: urlFragment ? `?${urlFragment}` : '' },
                    { replace: true }
                );
            }
        }
        try {
            localStorage.setItem(localStorageKey(layoutKey), JSON.stringify(filters));
        } catch { /* quota / private mode — non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, filtersReady, layoutKey]);

    // ── Cross-tab sync via `storage` event ─────────────────────────────────
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key !== localStorageKey(layoutKey)) return;
            try {
                const next = sanitize(JSON.parse(e.newValue) || emptyState(), accessibleIds, hasExplicitPropertyScope);
                setFilters(prev => sameShape(prev, next) ? prev : next);
            } catch { /* ignore */ }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutKey, accessibleIds.join(',')]);

    // ── Mutators ───────────────────────────────────────────────────────────
    const setDateRange = useCallback((dateRange) => {
        setFilters(prev => ({ ...prev, dateRange: dateRange || null }));
    }, []);

    const setPropertyIds = useCallback((propertyIds) => {
        setFilters(prev => ({ ...prev, propertyIds: dedupe(propertyIds) }));
    }, []);

    const setTenantIds = useCallback((tenantIds) => {
        setFilters(prev => ({ ...prev, tenantIds: dedupe(tenantIds) }));
    }, []);

    const reset = useCallback(() => {
        setFilters({
            ...emptyState(),
            dateRange: computePreset(DEFAULT_PRESET),
        });
    }, []);

    const value = useMemo(() => ({
        filters,
        filtersReady,
        layoutKey,
        accessiblePropertyIds: accessibleIds,
        hasExplicitPropertyScope,
        setDateRange,
        setPropertyIds,
        setTenantIds,
        reset,
    }), [filters, filtersReady, layoutKey, accessibleIds, hasExplicitPropertyScope, setDateRange, setPropertyIds, setTenantIds, reset]);

    return (
        <DashboardFiltersContext.Provider value={value}>
            {children}
        </DashboardFiltersContext.Provider>
    );
}

export function useDashboardFiltersContext() {
    const ctx = useContext(DashboardFiltersContext);
    if (!ctx) {
        // Return a safe stub so widgets/cards used outside the provider don't crash.
        return {
            filters: emptyState(),
            filtersReady: true,
            layoutKey: 'default',
            accessiblePropertyIds: [],
            hasExplicitPropertyScope: false,
            setDateRange: () => {},
            setPropertyIds: () => {},
            setTenantIds: () => {},
            reset: () => {},
        };
    }
    return ctx;
}

/* ─────────────────────────── helpers ─────────────────────────── */

function splitAccess(propertyAccess) {
    if (!propertyAccess) return [];
    return String(propertyAccess).split(',').map(s => s.trim()).filter(Boolean);
}

function dedupe(arr) {
    return Array.from(new Set((arr || []).filter(Boolean)));
}

function stripQuestion(search) {
    return (search || '').replace(/^\?/, '');
}

function sameShape(a, b) {
    if (a === b) return true;
    const adr = a?.dateRange, bdr = b?.dateRange;
    const drEq = (!adr && !bdr) || (adr && bdr && adr.from === bdr.from && adr.to === bdr.to);
    if (!drEq) return false;
    return arrEq(a.propertyIds, b.propertyIds)
        && arrEq(a.tenantIds, b.tenantIds)
        && arrEq(a.categories, b.categories);
}

function arrEq(a, b) {
    a = a || []; b = b || [];
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

/**
 * Pick initial state with priority: URL > localStorage > default(last30).
 * Validates property IDs against the user's current accessible set.
 */
function hydrate(search, layoutKey, accessibleIds, hasScope) {
    const urlState = fromUrlParams(search);
    const hasUrlState = !!(urlState.dateRange || urlState.propertyIds.length || urlState.tenantIds.length || urlState.categories.length);
    if (hasUrlState) return sanitize(urlState, accessibleIds, hasScope);

    try {
        const raw = localStorage.getItem(localStorageKey(layoutKey));
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && (parsed.dateRange || (parsed.propertyIds && parsed.propertyIds.length))) {
                return sanitize(parsed, accessibleIds, hasScope);
            }
        }
    } catch { /* ignore */ }

    return {
        ...emptyState(),
        dateRange: computePreset(DEFAULT_PRESET),
    };
}

function sanitize(state, accessibleIds, hasScope) {
    const next = {
        ...emptyState(),
        ...state,
    };
    // Strip empty / invalid date range
    if (!isValidRange(next.dateRange)) next.dateRange = null;
    // Default to Last 30d if nothing else
    if (!next.dateRange) next.dateRange = computePreset(DEFAULT_PRESET);
    // Only intersect property ids when the user has an explicit scope.
    // Admin / sadmin (no scope) keeps URL/localStorage property IDs verbatim;
    // the chip's "universe" is the API-fetched list.
    if (hasScope && Array.isArray(next.propertyIds) && next.propertyIds.length) {
        const accessSet = new Set(accessibleIds);
        next.propertyIds = next.propertyIds.filter(id => accessSet.has(id));
    } else {
        next.propertyIds = next.propertyIds || [];
    }
    next.tenantIds = next.tenantIds || [];
    next.categories = next.categories || [];
    return next;
}
